// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, X, Loader2, Tag } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { CouponValidationResult } from '@/types/affiliateCoupons';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface MembershipCouponInputProps {
  membershipSlug?: string;
  priceCents?: number;
  onCouponValidated?: (result: CouponValidationResult) => void;
  onCouponRemoved?: () => void;
  disabled?: boolean;
  isOneTimePayment?: boolean;
}

export const MembershipCouponInput: React.FC<MembershipCouponInputProps> = ({
  membershipSlug,
  priceCents,
  onCouponValidated,
  onCouponRemoved,
  disabled = false,
  isOneTimePayment = false
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<CouponValidationResult | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidationResult | null>(null);

  const validateCoupon = useCallback(async () => {
    if (!couponCode.trim()) return;

    console.log('🎫 [COUPON-INPUT] Starting coupon validation:', {
      couponCode: couponCode.trim(),
      membershipSlug,
      priceCents
    });

    setIsValidating(true);
    setValidationResult(null);

    try {
      const { data, error } = await invokeEdgeFunction('validate-affiliate-coupon-code', {
        body: {
          coupon_code: couponCode.trim(),
          membership_slug: membershipSlug,
          price_cents: priceCents
        }
      });

      console.log('🎫 [COUPON-INPUT] Validation response:', { data, error });

      if (error) throw error;

      setValidationResult(data);

      if (data.valid) {
        setAppliedCoupon(data);
        onCouponValidated?.(data);
        
        console.log('✅ [COUPON-INPUT] Coupon applied successfully:', {
          couponCode: couponCode.trim(),
          discountAmount: data.discount_amount,
          originalPrice: data.original_price,
          finalPrice: data.final_price
        });
        
        // Store affiliate ID for attribution
        if (data.coupon?.affiliate_id) {
          sessionStorage.setItem('applied_coupon_affiliate_id', data.coupon.affiliate_id);
          sessionStorage.setItem('applied_coupon_code', data.coupon.code);
        }
      } else {
        console.log('❌ [COUPON-INPUT] Invalid coupon:', data.error);
      }
    } catch (error) {
      console.error('❌ [COUPON-INPUT] Error validating coupon:', error);
      setValidationResult({
        valid: false,
        error: 'Failed to validate coupon code'
      });
    } finally {
      setIsValidating(false);
    }
  }, [couponCode, membershipSlug, priceCents, onCouponValidated]);

  const removeCoupon = useCallback(() => {
    setCouponCode('');
    setValidationResult(null);
    setAppliedCoupon(null);
    sessionStorage.removeItem('applied_coupon_affiliate_id');
    sessionStorage.removeItem('applied_coupon_code');
    onCouponRemoved?.();
  }, [onCouponRemoved]);

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Have a coupon code?</span>
      </div>

      {!appliedCoupon && (
        <div className="flex gap-2">
          <Input
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && validateCoupon()}
            disabled={disabled || isValidating}
            className="flex-1"
          />
          <Button
            onClick={validateCoupon}
            disabled={disabled || isValidating || !couponCode.trim()}
            variant="outline"
            size="sm"
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Apply'
            )}
          </Button>
        </div>
      )}

      {/* Validation Messages */}
      {validationResult && !appliedCoupon && (
        <Alert variant={validationResult.valid ? "default" : "destructive"}>
          <div className="flex items-center gap-2">
            {validationResult.valid ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <X className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription>
              {validationResult.valid 
                ? `Coupon applied! You'll save ${formatPrice(validationResult.discount_amount || 0)}`
                : validationResult.error || 'Invalid coupon code'
              }
            </AlertDescription>
          </div>
        </Alert>
      )}

      {/* Applied Coupon Display */}
      {appliedCoupon && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <span className="font-medium">Coupon "{appliedCoupon.coupon?.code}" applied!</span>
              <br />
              <span className="text-sm text-green-700">
                {appliedCoupon.coupon?.discount_type === 'percentage' 
                  ? `${appliedCoupon.coupon.discount_value}% off`
                  : `$${appliedCoupon.coupon?.discount_value} off`
                }
                {!isOneTimePayment && ` first month, then ${formatPrice(appliedCoupon.original_price || 0)}/month`}
              </span>
            </div>
            <Button
              onClick={removeCoupon}
              variant="ghost"
              size="sm"
              disabled={disabled}
            >
              Remove
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Pricing Summary */}
      {appliedCoupon && priceCents && (
        <div className="bg-muted p-3 rounded-md">
          <div className="flex justify-between text-sm">
            <span>Original Price:</span>
            <span className="line-through text-muted-foreground">
              {formatPrice(appliedCoupon.original_price || 0)}
            </span>
          </div>
          <div className="flex justify-between text-sm font-medium">
            <span>{isOneTimePayment ? 'Your Price:' : 'First Month:'}</span>
            <span className="text-green-600">{formatPrice(appliedCoupon.final_price || 0)}</span>
          </div>
          {!isOneTimePayment && (
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Then:</span>
              <span>{formatPrice(appliedCoupon.original_price || 0)}/month</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};